import type { IsNever } from '../index.js';

/**
 * You can provide {@link TReturn} manually, for example:
 * ```ts
 * declare const value: 123;
 * const valueAsNumber = as<number>(value);
 * ```
 *
 * Or you can let TypeScript infer type parameters, for example:
 * ```ts
 * declare const value: 123;
 * const valueAsNumber: number = as(value);
 * ```
 *
 * **Q:** Why use this? Why not just use `value as number`?
 * **A:** Because it's invaluable for type safe inference of generic and
 * conditional types, and avoids a lot of repetition!
 *
 * For example, say we have a function with a conditional return type, with the
 * following signature:
 * ```ts
 * type ReturnObjectIfItHasFooPropertyFunction = {
 *   <T extends object>(obj: T):
 *     // This return type will be inferred by `as` automagically:
 *     keyof T extends 'foo' ? T : never;
 * }
 * ```
 *
 * If we try to implement this function without {@link as}, TS will fail:
 * ```ts
 * const broken: ReturnObjectIfItHasFooPropertyFunction = obj => {
 *   if ('foo' in obj) return obj;
 *   throw new Error('Object does not have a "foo" property');
 * };
 * // TS2322 error:
 * // Type '<T extends object>(obj: T) => T & Record<"foo", unknown>'
 * // is not assignable to type 'ReturnObjectIfItHasFooPropertyFunction'.
 * //  ↳ Type 'T & Record<"foo", unknown>'
 * //    is not assignable to type 'keyof T extends "foo" ? T : never'.
 * ```
 *
 * If instead we use {@link as}, TS will succeed:
 * ```ts
 * const fixed: ReturnObjectIfItHasFooPropertyFunction = obj => {
 *   if ('foo' in obj) return as(obj);
 *   throw new Error('Object does not have a "foo" property');
 * };
 * ```
 *
 * Because of {@link as}'s type inference, it automatically adopts the
 * following signature when returned in `fixed`:
 * ```ts
 * function as<
 *   T & Record<"foo", unknown>,
 *   keyof T extends "foo" ? T : never,
 * >(
 *   input: T & Record<"foo", unknown>
 * ):
 *   keyof T extends "foo" ? T : never;
 * ```
 *
 * Without {@link as}, you'd have to copy the return type to do manual casting:
 * ```ts
 * type TReturn = keyof typeof obj extends 'foo' ? typeof obj : never;
 * if ('foo' in obj) return obj as TReturn;
 * ```
 *
 * Not only do you now have twice the maintenance load, but it's also not type
 * safe, since TS will allow you to return invalid objects, for example:
 * ```ts
 * return { notFoo: 123 } as TReturn; // compiles just fine → yikes!!
 * ```
 *
 * When using {@link as}, it will not allow such shenanigans:
 * ```ts
 * return as({ notFoo: 123 }); // breaks compilation, as return type is `never`
 * ```
 */
export function as<TReturn = never>(
  input: TReturn extends infer TInput ? TInput : never
): TReturn;

export function as<TInput, TReturn = never>(
  input: IsNever<TReturn> extends true ? never : TInput
): TInput extends TReturn ? TReturn : never;

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
export function as<TInput, TReturn = never>(input: TInput): TReturn;

export function as<TInput extends TReturn, TReturn>(input: TInput): TReturn {
  return input;
}
